/* ===========================================================
   Hooks de instrumentação da landing — decisions.md §34.
   Vivem no ciclo das TELAS (espelham o useReveal): a `Landing` monta seções
   de forma assíncrona (gated por dados), então o observer precisa reagir ao
   DOM real. As telas não ganham lógica de analytics inline — só chamam o hook;
   nomes de evento e transporte ficam isolados em events.ts / mixpanel.ts.
   Os cliques de CTA são captados por DELEGAÇÃO via atributo `data-cta` nos
   botões (metadado apresentacional), sem mudar assinatura de callbacks.
   =========================================================== */
import { useEffect } from "react";
import {
  ctaClicado,
  faqAberta,
  marcarSuperficie,
  navClicada,
  paginaVista,
  scrollAtingido,
  secaoVista,
} from "./events";

const SCROLL_MARCOS = [25, 50, 75, 100] as const;

/**
 * Rastreia o comportamento de acesso da LANDING (home). Chamado 1x pela tela
 * `Landing`. Cobre: init, "Página Vista", "Seção Vista" (IO + MutationObserver
 * p/ seções que chegam com os dados), "Scroll Atingido" (25/50/75/100%) e
 * cliques delegados — [data-cta] → "CTA Clicado", a[href^="#"] → "Nav Clicada",
 * <details.lp-faq-item> → "FAQ Aberta".
 */
export function useLandingTracking(): void {
  useEffect(() => {
    marcarSuperficie("landing");
    paginaVista(window.location.pathname);

    const limpar: Array<() => void> = [];

    // Seções vistas (1x cada). IO dispara e desobserva; MO captura as seções
    // gated por dados, que montam depois do 1º render. Guardado: ambientes sem
    // essas APIs (ex.: jsdom nos testes) apenas deixam de rastrear seção.
    if (typeof IntersectionObserver !== "undefined") {
      const observadas = new WeakSet<Element>();
      const io = new IntersectionObserver(
        (entries) => {
          for (const e of entries) {
            if (e.isIntersecting && e.target.id) {
              secaoVista(e.target.id);
              io.unobserve(e.target);
            }
          }
        },
        { threshold: 0.01 },
      );
      const varrer = (): void => {
        document.querySelectorAll("main section[id]").forEach((s) => {
          if (!observadas.has(s)) {
            observadas.add(s);
            io.observe(s);
          }
        });
      };
      varrer();
      const main = document.querySelector("main");
      const mo = typeof MutationObserver !== "undefined" ? new MutationObserver(varrer) : null;
      if (main && mo) mo.observe(main, { childList: true, subtree: true });
      limpar.push(() => {
        io.disconnect();
        mo?.disconnect();
      });
    }

    // Profundidade de rolagem.
    const batidos = new Set<number>();
    const onScroll = (): void => {
      const doc = document.documentElement;
      const alcance = doc.scrollHeight - doc.clientHeight;
      if (alcance <= 0) return;
      const pct = (doc.scrollTop / alcance) * 100;
      for (const m of SCROLL_MARCOS) {
        if (pct >= m && !batidos.has(m)) {
          batidos.add(m);
          scrollAtingido(m);
        }
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    limpar.push(() => window.removeEventListener("scroll", onScroll));

    // Cliques delegados — CTA (data-cta) e nav âncora.
    const onClick = (ev: MouseEvent): void => {
      const alvo = ev.target;
      if (!(alvo instanceof Element)) return;
      const cta = alvo.closest<HTMLElement>("[data-cta]");
      if (cta?.dataset.cta) {
        ctaClicado(cta.dataset.cta);
        return;
      }
      const ancora = alvo.closest('a[href^="#"]');
      if (ancora) navClicada(ancora.getAttribute("href") ?? "");
    };
    document.addEventListener("click", onClick, true);
    limpar.push(() => document.removeEventListener("click", onClick, true));

    // FAQ aberta — o evento `toggle` não borbulha; captura-se na fase de captura.
    const onToggle = (ev: Event): void => {
      const alvo = ev.target;
      if (
        alvo instanceof HTMLDetailsElement &&
        alvo.open &&
        alvo.classList.contains("lp-faq-item")
      ) {
        const q = alvo.querySelector("summary")?.textContent?.trim();
        if (q) faqAberta(q);
      }
    };
    document.addEventListener("toggle", onToggle, true);
    limpar.push(() => document.removeEventListener("toggle", onToggle, true));

    return () => limpar.forEach((fn) => fn());
  }, []);
}
